import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectLuminaryThumbnail } from '../thumbnail-previews';

import { getBlueprint } from './blueprints-selectors';

import type { RootState } from 'js/src/libs/RootState';

// Custom hook to create a canvas of an image
export const useCanvasPreview = (imageUrl?: string) => {
    const [canvasElement, setCanvasElement] = useState<HTMLCanvasElement | null>(null);

    useEffect(() => {
        if (!imageUrl) {
            return;
        }
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);
            setCanvasElement(canvas);
        };
        img.src = imageUrl;
    }, [imageUrl]);

    return canvasElement;
};

/**
 * Hook to get a canvas preview for a blueprint, with Luminary fallback.
 * Use this when you have posterUrl and blueprintId directly (e.g., from API response).
 * Prefers poster_url (pre-rendered) over Luminary-generated thumbnails for performance.
 */
export const useBlueprintThumbnailWithFallback = (
    posterUrl: string | null | undefined,
    blueprintId: number,
) => {
    const posterCanvas = useCanvasPreview(posterUrl ?? undefined);
    const luminaryCanvasPreview = useSelector((state: RootState) =>
        selectLuminaryThumbnail(state, blueprintId),
    );
    return posterCanvas || luminaryCanvasPreview;
};

/**
 * Hook to get a canvas preview of a blueprint from Redux state.
 * Use this when you only have the blueprintId and the blueprints are loaded in Redux.
 * Luminary previews are generated by calling generateBlueprintThumbnailsOperation for blueprints without a poster url.
 */
export const useBlueprintThumbnail = (blueprintId: number) => {
    const blueprint = useSelector((state: RootState) => getBlueprint(state, blueprintId));
    return useBlueprintThumbnailWithFallback(blueprint?.poster_url, blueprintId);
};
